import { FbaCalculator, CONVERSION_RATES } from './fba-calculator';
import type {
  CostCalculationInput,
  CostCalculationResult,
  FbaCalculationResult
} from '@/types';

export class CostCalculator {
  /**
   * 计算成本分析
   */
  static calculate(input: CostCalculationInput): CostCalculationResult {
    // 转换尺寸和重量单位
    const dimensionsInInches = FbaCalculator.convertDimensionsToInches(input.dimensions);
    const weightInLbs = FbaCalculator.convertWeightToLbs(input.weight);
    const weightInKg = input.weight.unit === 'kg'
      ? input.weight.value
      : input.weight.unit === 'g'
        ? input.weight.value / 1000
        : input.weight.unit === 'lb'
          ? input.weight.value * 0.45359237
          : input.weight.value * 0.0283495; // oz to kg

    // 计算体积重量（kg）
    const volumeCubicInches = dimensionsInInches.length * dimensionsInInches.width * dimensionsInInches.height;
    const volumetricWeightKg = (volumeCubicInches / 139) * 0.45359237; // 转换为kg

    // 计费重量（kg）
    const billingWeightKg = Math.max(weightInKg, volumetricWeightKg);

    // 计算FBA费用
    const fbaInput = {
      dimensions: input.dimensions,
      weight: input.weight,
      price: input.sellingPriceUSD,
      productType: input.productType,
      isPeakSeason: input.isPeakSeason
    };

    const fbaResult: FbaCalculationResult = FbaCalculator.calculate(fbaInput);

    // 成本计算（美元）
    const purchaseCostUSD = input.purchasePriceCNY / input.exchangeRate;
    const shippingCostUSD = (input.shippingPriceCNYPerKg * billingWeightKg) / input.exchangeRate;
    const fbaFeeUSD = fbaResult.totalFee;
    const commissionUSD = input.sellingPriceUSD * (input.commissionRate / 100);
    const fbaStorageUSD = input.sellingPriceUSD * (input.fbaStorageRate / 100);
    const otherFeesUSD = input.sellingPriceUSD * (input.otherFeesRate / 100);
    const vatUSD = input.sellingPriceUSD * (input.vatRate / 100);

    // 总成本
    const totalCostUSD = purchaseCostUSD + shippingCostUSD + fbaFeeUSD +
                        commissionUSD + fbaStorageUSD + otherFeesUSD + vatUSD;

    // 考虑退货损失后的调整总成本
    const adjustedTotalCostUSD = totalCostUSD / (1 - input.returnRate / 100);

    // 利润计算
    const grossProfitUSD = input.sellingPriceUSD - adjustedTotalCostUSD;
    const profitMargin = (grossProfitUSD / input.sellingPriceUSD) * 100;

    return {
      fbaFee: fbaResult,
      costs: {
        purchaseCostUSD: this.round(purchaseCostUSD, 4),
        shippingCostUSD: this.round(shippingCostUSD, 4),
        fbaFeeUSD: this.round(fbaFeeUSD, 2),
        commissionUSD: this.round(commissionUSD, 2),
        fbaStorageUSD: this.round(fbaStorageUSD, 2),
        otherFeesUSD: this.round(otherFeesUSD, 2),
        vatUSD: this.round(vatUSD, 2),
        totalCostUSD: this.round(totalCostUSD, 2),
        adjustedTotalCostUSD: this.round(adjustedTotalCostUSD, 2)
      },
      profit: {
        grossProfitUSD: this.round(grossProfitUSD, 2),
        profitMargin: this.round(profitMargin, 2)
      },
      weights: {
        actualWeightKg: this.round(weightInKg, 3),
        volumetricWeightKg: this.round(volumetricWeightKg, 3),
        billingWeightKg: this.round(billingWeightKg, 3)
      }
    };
  }

  /**
   * 盈亏平衡分析
   */
  static calculateBreakEven(input: Omit<CostCalculationInput, 'sellingPriceUSD'>): {
    breakEvenPriceUSD: number;
    breakEvenPriceCNY: number;
    recommendedPriceUSD: number;
    recommendedPriceCNY: number;
  } {
    // 计算实际重量用于FBA费用计算
    const dimensionsInInches = FbaCalculator.convertDimensionsToInches(input.dimensions);
    const weightInLbs = FbaCalculator.convertWeightToLbs(input.weight);
    const weightInKg = input.weight.unit === 'kg'
      ? input.weight.value
      : input.weight.unit === 'g'
        ? input.weight.value / 1000
        : input.weight.unit === 'lb'
          ? input.weight.value * 0.45359237
          : input.weight.value * 0.0283495;

    // 计算体积重量（kg）
    const volumeCubicInches = dimensionsInInches.length * dimensionsInInches.width * dimensionsInInches.height;
    const volumetricWeightKg = (volumeCubicInches / 139) * 0.45359237;
    const billingWeightKg = Math.max(weightInKg, volumetricWeightKg);

    // 计算固定成本（不随售价变化的部分）
    const fbaInput = {
      dimensions: input.dimensions,
      weight: input.weight,
      price: 10, // 临时价格用于计算FBA费用基础
      productType: input.productType,
      isPeakSeason: input.isPeakSeason
    };

    const fbaResult = FbaCalculator.calculate(fbaInput);
    const baseFbaFee = fbaResult.baseFee;

    const purchaseCostUSD = input.purchasePriceCNY / input.exchangeRate;
    const shippingCostUSD = (input.shippingPriceCNYPerKg * billingWeightKg) / input.exchangeRate;
    const otherCostsUSD = baseFbaFee * (input.fbaStorageRate / 100 + input.otherFeesRate / 100 + input.vatRate / 100);

    // 盈亏平衡价格计算 - 修正变量成本率计算
    const commissionRate = input.commissionRate / 100;
    const returnRate = input.returnRate / 100;
    const variableCostRate = commissionRate + returnRate * (1 + commissionRate);
    const fixedCostsUSD = purchaseCostUSD + shippingCostUSD + baseFbaFee + otherCostsUSD;

    const breakEvenPriceUSD = fixedCostsUSD / (1 - variableCostRate);
    const breakEvenPriceCNY = breakEvenPriceUSD * input.exchangeRate;

    // 推荐价格（盈亏平衡价格 + 20%利润率）
    const recommendedPriceUSD = breakEvenPriceUSD * 1.2;
    const recommendedPriceCNY = recommendedPriceUSD * input.exchangeRate;

    return {
      breakEvenPriceUSD: this.round(breakEvenPriceUSD, 2),
      breakEvenPriceCNY: this.round(breakEvenPriceCNY, 2),
      recommendedPriceUSD: this.round(recommendedPriceUSD, 2),
      recommendedPriceCNY: this.round(recommendedPriceCNY, 2)
    };
  }

  /**
   * 批量计算不同售价的利润率
   */
  static calculateProfitabilityRange(
    baseInput: CostCalculationInput,
    priceRange: { min: number; max: number; step: number }
  ): Array<{
    price: number;
    profit: number;
    margin: number;
    costs: CostCalculationResult['costs'];
  }> {
    const results = [];

    for (let price = priceRange.min; price <= priceRange.max; price += priceRange.step) {
      const input = { ...baseInput, sellingPriceUSD: price };
      const result = this.calculate(input);

      results.push({
        price,
        profit: result.profit.grossProfitUSD,
        margin: result.profit.profitMargin,
        costs: result.costs
      });
    }

    return results;
  }

  /**
   * 成本优化建议
   */
  static generateOptimizationSuggestions(result: CostCalculationResult): Array<{
    category: 'shipping' | 'packaging' | 'pricing' | 'fees';
    priority: 'high' | 'medium' | 'low';
    suggestion: string;
    potentialSavings: number;
  }> {
    const suggestions = [];
    const { costs, profit } = result;

    // 运输成本优化
    if (costs.shippingCostUSD > costs.purchaseCostUSD * 0.3) {
      suggestions.push({
        category: 'shipping',
        priority: 'high',
        suggestion: '运输成本过高，建议优化包装或寻找更便宜的物流方案',
        potentialSavings: costs.shippingCostUSD * 0.2
      });
    }

    // 采购成本优化
    if (costs.purchaseCostUSD > costs.totalCostUSD * 0.6) {
      suggestions.push({
        category: 'packaging',
        priority: 'medium',
        suggestion: '采购成本占比较高，建议与供应商谈判寻找更优价格',
        potentialSavings: costs.purchaseCostUSD * 0.1
      });
    }

    // 定价建议
    if (profit.profitMargin < 15) {
      suggestions.push({
        category: 'pricing',
        priority: 'high',
        suggestion: `利润率偏低(${profit.profitMargin.toFixed(1)}%)，建议提高售价或降低成本`,
        potentialSavings: costs.totalCostUSD * 0.05
      });
    }

    // 佣金优化
    if (costs.commissionUSD > costs.totalCostUSD * 0.2) {
      suggestions.push({
        category: 'fees',
        priority: 'medium',
        suggestion: '佣金成本较高，可考虑优化产品分类或参加佣金减免计划',
        potentialSavings: costs.commissionUSD * 0.15
      });
    }

    return suggestions.sort((a, b) => {
      const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * 四舍五入辅助函数
   */
  private static round(value: number, decimals: number): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  /**
   * 汇率影响分析
   */
  static analyzeExchangeRateImpact(
    input: CostCalculationInput,
    rateRange: { min: number; max: number; step: number }
  ): Array<{
    exchangeRate: number;
    purchaseCostUSD: number;
    profitMargin: number;
    impact: number;
  }> {
    const results = [];
    const baseResult = this.calculate(input);
    const baseMargin = baseResult.profit.profitMargin;

    for (let rate = rateRange.min; rate <= rateRange.max; rate += rateRange.step) {
      const testInput = { ...input, exchangeRate: rate };
      const testResult = this.calculate(testInput);

      results.push({
        exchangeRate: rate,
        purchaseCostUSD: testResult.costs.purchaseCostUSD,
        profitMargin: testResult.profit.profitMargin,
        impact: testResult.profit.profitMargin - baseMargin
      });
    }

    return results;
  }
}